import React, {
	FC, useEffect, useMemo, useState,
} from 'react';
import { DashboardFrame } from '@aleph/supergrid';
import {
	AvatarEntity, Avatar, AvatarSize, NoValuePlaceholder, StatusToggle,
} from '@aleph/shared-components';
import {
	COLOR_PALETTE, GenericStatus, RedditLevel, getClientRecursively, isPendingAdAccount,
} from 'wb-reddit/app/routes/superGrid/utils';
import classNames from 'classnames';
import redditLogo from 'wb-reddit/assets/reddit-logo.png';
import { ICellRendererParams } from 'ag-grid-community';

interface IStatusCellRendererParams extends ICellRendererParams {
	statusPending?: GenericStatus;
}

const StatusCellRenderer: FC<IStatusCellRendererParams> = ({
	value, node, setValue, data,
}) => {
	const [currStatus, setCurrStatus] = useState<GenericStatus>(data?.statusPending);

	useEffect(() => {
		setCurrStatus(data?.statusPending);
	}, [data?.statusPending]);

	const isDisabledAdAccount = useMemo(() => {
		const client = getClientRecursively(data);

		return !!isPendingAdAccount(client);
	}, [data]);

	const getCurrentStatus = useMemo(() => {
		const pendingStatusMap = {
			[GenericStatus.Active]:   GenericStatus.PendingActive,
			[GenericStatus.Warning]:  GenericStatus.PendingWarning,
			[GenericStatus.Error]:    GenericStatus.PendingError,
			[GenericStatus.Paused]:   GenericStatus.PendingPaused,
			[GenericStatus.Archived]: GenericStatus.PendingArchived,
			[GenericStatus.Deleted]:  GenericStatus.PendingDeleted,
		};

		if (data?.hasErrors) {
			return isDisabledAdAccount ? GenericStatus.Error : pendingStatusMap[GenericStatus.Error];
		}

		if (isDisabledAdAccount) {
			return value ?? GenericStatus.Paused;
		}

		// In case the status is same as pending status we show the status that is already synced.
		if (value === currStatus || !currStatus) {
			return Object.values(GenericStatus).includes(value) ? value : GenericStatus.Paused;
		}

		return pendingStatusMap[currStatus];
	}, [currStatus, data?.hasErrors, isDisabledAdAccount]);

	const setStatus = () => {
		const statusMap = {
			[GenericStatus.Active]: GenericStatus.Paused,
			[GenericStatus.Paused]: GenericStatus.Active,
		};

		return statusMap[value] || GenericStatus.Active;
	};

	const changeStatus = () => {
		setCurrStatus(setStatus());
		setValue?.(setStatus());
	};

	const isDashboardView = data?.isDashboardView && !node.expanded;
	const disabled = (!!currStatus && value !== currStatus) || isDisabledAdAccount;

	if (data?.isCreate) {
		return null;
	}

	if (data?.level === RedditLevel.BREAKDOWN) {
		return <NoValuePlaceholder />;
	}

	return (
		<>
			{
				<div className={classNames(
					'w-full flex justify-center h-[64px] max-h-full items-center',
					disabled && 'cursor-not-allowed',
				)}
				>
					{
						data?.level !== RedditLevel.AD_ACCOUNT ? <StatusToggle
							onChange={changeStatus}
							currentStatus={getCurrentStatus}
							colorPalette={COLOR_PALETTE}
							disabled={disabled}
							tooltipContent={
								isDisabledAdAccount ? 'Status can not be changed while pending Advertiser verification'
									: 'Pending sync to reddit'
							}
							checkedStatuses={[
								GenericStatus.PendingActive,
								GenericStatus.Active,
								GenericStatus.PendingWarning,
								GenericStatus.Warning,
							]}
						/> : <Avatar
							alt='client profile'
							src={data?.clientProfilePicture ?? redditLogo}
							size={node.rowHeight && node.rowHeight < 40 ? AvatarSize.XS : AvatarSize.S}
							entityType={AvatarEntity.Account}
						/>
					}
				</div>
			}
			{isDashboardView && <DashboardFrame />}
		</>
	);
};

export default StatusCellRenderer;
